import { motion } from "framer-motion";
import { BrainCircuit, LineChart, Users, Sparkles, BadgeCheck } from "lucide-react";
import { ChapterShell } from "../ChapterShell";
import { FadeUp } from "../Reveal";
import { HEADSHOT_FALLBACK } from "../../data/content";
import { imgFallback } from "../../utils";

const AI_FIELD = [
  { icon: LineChart, label: "Predictive Analytics" },
  { icon: Users, label: "Customer Insights" },
  { icon: Sparkles, label: "Generative AI" },
  { icon: BrainCircuit, label: "Market Intelligence" },
];

const CERTS = [
  "Certified AI Generalist",
  "AI for Business Professionals Masterclass",
  "Training Program on AI Powered Marketing — MSME Technology Centre, Govt. of India",
];

export const ChapterNine = () => (
  <ChapterShell
    n="09"
    id="chapter-9"
    era="Now"
    title="The AI Marketing Era"
    quote="After years of understanding markets, brands, doctors and patients, the next evolution is combining human marketing intelligence with artificial intelligence."
    dark
  >
    <div className="grid grid-cols-1 items-center gap-14 lg:grid-cols-12">
      <div className="lg:col-span-6">
        <FadeUp>
          <h3 className="font-display text-3xl font-black tracking-tight text-white md:text-5xl">
            The next chapter is <span className="text-[#00E5FF]">AI.</span>
          </h3>
        </FadeUp>

        <div className="mt-10 grid grid-cols-2 gap-4 sm:grid-cols-3">
          {AI_FIELD.map((f, i) => (
            <motion.div
              key={f.label}
              initial={{ opacity: 0, y: 26 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.55, delay: i * 0.08 }}
              className="glass-dark flex flex-col items-center gap-3 rounded-2xl px-4 py-6 text-center"
            >
              <f.icon size={20} className="text-[#00E5FF]" />
              <span className="font-display text-[10px] font-bold uppercase tracking-[0.16em] text-white/80">
                {f.label}
              </span>
            </motion.div>
          ))}
        </div>

        <div className="mt-10 space-y-3">
          {CERTS.map((c, i) => (
            <motion.div
              key={c}
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.6, delay: i * 0.12 }}
              className="glass-dark flex items-center gap-4 rounded-2xl px-6 py-4"
            >
              <BadgeCheck size={18} className="shrink-0 text-[#00BFA5]" />
              <span className="text-sm font-semibold text-white/85">{c}</span>
            </motion.div>
          ))}
        </div>
      </div>

      <div className="lg:col-span-6">
        <FadeUp delay={0.15} className="relative mx-auto max-w-sm">
          <div className="absolute -inset-8 rounded-full bg-[#00E5FF]/15 blur-3xl" />
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 40, repeat: Infinity, ease: "linear" }}
            className="absolute inset-0 rounded-full border border-dashed border-[#00E5FF]/25"
            style={{ margin: "-1.5rem" }}
          />
          <div className="glass-dark relative overflow-hidden rounded-[2rem] p-2">
            <img
              src="/assets/avatar-future.png"
              onError={imgFallback(HEADSHOT_FALLBACK)}
              alt="Looking toward the AI-powered future"
              data-testid="chapter9-avatar"
              className="w-full rounded-[1.6rem] object-cover"
            />
          </div>
          <p className="mt-5 text-center text-[11px] font-bold uppercase tracking-[0.25em] text-white/40">
            Human intelligence × artificial intelligence
          </p>
        </FadeUp>
      </div>
    </div>
  </ChapterShell>
);
